import { useEffect, useState } from "react";
import { ArrowDownToLine, RefreshCw, AlertTriangle, TrendingUp } from "lucide-react";
import { API_BASE } from "@/lib/utils";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

export default function Finance() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [controls, setControls] = useState(defaultCloseControls());
  const [savingControls, setSavingControls] = useState(false);
  const [controlsNote, setControlsNote] = useState("");
  const [refundForm, setRefundForm] = useState({ eventId: "", orderId: "", attendeeEmail: "", amount: "", reason: "" });
  const [refundBusy, setRefundBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [summaryRes, controlsRes] = await Promise.all([
          fetch(`${API_BASE}/api/admin/finance/summary`),
          fetch(`${API_BASE}/api/state/finance.close-controls`),
        ]);
        if (!summaryRes.ok) throw new Error(`Failed to load finance summary (${summaryRes.status})`);
        const data = await summaryRes.json();
        const controlsPayload = controlsRes.ok ? await controlsRes.json().catch(() => null) : null;
        if (!cancelled) {
          setPayload(data);
          setControls(normalizeControls(controlsPayload?.value));
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load finance summary");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const kpis = payload?.kpis || {};
  const byEvent = Array.isArray(payload?.byEvent) ? payload.byEvent : [];
  const webhookSeries = Array.isArray(payload?.webhookSeries) ? payload.webhookSeries : [];
  const serviceHealth = Array.isArray(payload?.serviceHealth) ? payload.serviceHealth : [];
  const refundQueue = Array.isArray(payload?.refundQueue) ? payload.refundQueue : [];
  const activePeriod = controls.period || currentPeriod();
  const checklist = controls.checklist || {};
  const allChecksDone = CLOSE_CHECKLIST.every((item) => checklist[item.id]);
  const isLocked = Boolean(controls.locked);

  const updateChecklist = (id, checked) => {
    if (isLocked) return;
    setControls((prev) => ({
      ...normalizeControls(prev),
      checklist: { ...(prev?.checklist || {}), [id]: checked },
    }));
  };

  const saveCloseControls = async (nextControls, successNote = "Finance controls saved") => {
    setSavingControls(true);
    setControlsNote("");
    try {
      const normalized = normalizeControls(nextControls);
      const res = await fetch(`${API_BASE}/api/state/finance.close-controls`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ value: normalized }),
      });
      if (!res.ok) throw new Error(`Failed to save finance controls (${res.status})`);
      setControls(normalized);
      setControlsNote(successNote);
    } catch (err) {
      setControlsNote(err?.message || "Failed to save finance controls");
    } finally {
      setSavingControls(false);
    }
  };

  const togglePeriodLock = async () => {
    const normalized = normalizeControls(controls);
    if (normalized.locked) {
      await saveCloseControls(
        {
          ...normalized,
          locked: false,
          lockedAt: null,
          lockedBy: null,
        },
        `${normalized.period} unlocked`,
      );
      return;
    }
    if (!allChecksDone) {
      setControlsNote("Complete all month-end checks before locking this period.");
      return;
    }
    await saveCloseControls(
      {
        ...normalized,
        locked: true,
        lockedAt: new Date().toISOString(),
        lockedBy: "admin@aivory.io",
      },
      `${normalized.period} locked`,
    );
  };

  const persistChecklist = async () => {
    await saveCloseControls(controls, "Checklist saved");
  };
  const refreshFinance = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/finance/summary`);
      if (!res.ok) throw new Error(`Failed to load finance summary (${res.status})`);
      const data = await res.json();
      setPayload(data);
    } catch {
      // keep existing view
    }
  };
  const requestRefund = async () => {
    if (!refundForm.eventId || !refundForm.amount) return;
    try {
      setRefundBusy(true);
      const res = await fetch(`${API_BASE}/api/admin/refunds/request`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          event_id: refundForm.eventId,
          order_id: refundForm.orderId,
          attendee_email: refundForm.attendeeEmail,
          amount: Number(refundForm.amount || 0),
          reason: refundForm.reason,
        }),
      });
      if (!res.ok) throw new Error(`Refund request failed (${res.status})`);
      await refreshFinance();
      setRefundForm((prev) => ({ ...prev, orderId: "", attendeeEmail: "", amount: "", reason: "" }));
    } catch (err) {
      setControlsNote(err?.message || "Could not request refund");
    } finally {
      setRefundBusy(false);
    }
  };
  const decideRefund = async (id, decision) => {
    try {
      setRefundBusy(true);
      const res = await fetch(`${API_BASE}/api/admin/refunds/${id}/decision`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ decision }),
      });
      if (!res.ok) throw new Error(`Refund decision failed (${res.status})`);
      await refreshFinance();
    } catch (err) {
      setControlsNote(err?.message || "Could not update refund");
    } finally {
      setRefundBusy(false);
    }
  };
  const exportLedger = () => {
    const rows = [
      ["event_id", "event_title", "venue", "tickets", "addons", "refunds", "net"],
      ...byEvent.map((e) => [
        e?.id || "",
        e?.title || "",
        e?.venue || "",
        Number(e?.tickets || 0),
        Number(e?.addons || 0),
        Number(e?.refunds || 0),
        Number(e?.net || 0),
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replaceAll("\"", "\"\"")}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `finance-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1500px] mx-auto" data-testid="finance-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-overline">Workspace · finance</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Finance</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2">Across all events · YTD 2026</p>
        </div>
        <button className="btn-secondary" onClick={exportLedger}><ArrowDownToLine size={14} /> Export ledger</button>
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
        <KPI k="Gross revenue" v={loading ? "..." : `$${((kpis.grossRevenue || 0) / 1000).toFixed(1)}k`} delta="From ticket engine sales" />
        <KPI k="Refunds issued" v={loading ? "..." : `$${(kpis.refundsIssued || 0).toLocaleString()}`} delta="From recorded finance state" />
        <KPI k="Net revenue" v={loading ? "..." : `$${((kpis.netRevenue || 0) / 1000).toFixed(1)}k`} delta="Gross minus recorded refunds" />
        <KPI k="Tax collected" v={loading ? "..." : `$${Number(kpis.taxCollected || 0).toLocaleString()}`} delta={`${String(kpis.taxMode || "exclusive").toUpperCase()} · ${Number(kpis.taxRatePct || 0)}%`} />
        <KPI k="Pending refunds" v={loading ? "..." : `${Number(kpis.pendingRefunds || 0).toLocaleString()}`} delta="Awaiting approval decision" />
      </div>

      {/* Close controls */}
      <h2 className="font-heading text-2xl mt-14 mb-4">Close checklist and period lock</h2>
      <div className="surface p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-overline">Active period</div>
            <div className="font-mono text-lg mt-1">{activePeriod}</div>
            <div className="text-xs text-[var(--text-tertiary)] mt-1">
              {isLocked
                ? `Locked ${formatDateTime(controls.lockedAt)} by ${controls.lockedBy || "finance"}`
                : "Open for reconciliation updates"}
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={persistChecklist} disabled={savingControls || isLocked}>
              {savingControls ? "Saving..." : "Save checklist"}
            </button>
            <button className={isLocked ? "btn-secondary" : "btn-primary"} onClick={togglePeriodLock} disabled={savingControls}>
              {isLocked ? "Unlock period" : "Lock period"}
            </button>
          </div>
        </div>

        <div className="mt-5 grid md:grid-cols-2 gap-3">
          {CLOSE_CHECKLIST.map((item) => (
            <label key={item.id} className="flex items-start gap-3 rounded-xl border border-[var(--border-default)] p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(checklist[item.id])}
                onChange={(ev) => updateChecklist(item.id, ev.target.checked)}
                disabled={isLocked}
                className="mt-1 accent-[var(--primary)]"
              />
              <div>
                <div className="text-sm text-[#F4F4F5]">{item.label}</div>
                <div className="text-xs text-[var(--text-tertiary)] mt-1">{item.help}</div>
              </div>
            </label>
          ))}
        </div>
        {controlsNote ? <div className="mt-3 text-xs text-[var(--text-secondary)]">{controlsNote}</div> : null}
      </div>

      {/* By event */}
      <h2 className="font-heading text-2xl mt-14 mb-4">By event</h2>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-4 text-left">Event</th>
              <th className="p-4 text-right">Tickets</th>
              <th className="p-4 text-right">Add-ons</th>
              <th className="p-4 text-right">Refunds</th>
              <th className="p-4 text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {byEvent.map((e, idx) => (
              <tr key={e?.id || idx} className="border-b border-[var(--border-default)] hover:bg-[var(--bg-elev)]">
                <td className="p-4"><div className="font-medium">{e?.title || "Untitled event"}</div><div className="text-xs text-[var(--text-tertiary)] font-mono">{e?.venue || "—"}</div></td>
                <td className="p-4 text-right font-mono">${(Number(e?.tickets || 0) / 1000).toFixed(1)}k</td>
                <td className="p-4 text-right font-mono">${(Number(e?.addons || 0) / 1000).toFixed(1)}k</td>
                <td className="p-4 text-right font-mono text-[var(--error)]">-${(Number(e?.refunds || 0) / 1000).toFixed(1)}k</td>
                <td className="p-4 text-right font-mono text-[var(--primary)]">${(Number(e?.net || 0) / 1000).toFixed(1)}k</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Refunds + webhooks */}
      <div className="mt-12 grid md:grid-cols-2 gap-6">
        <div className="surface p-6">
          <div className="flex items-center justify-between">
            <div className="text-overline flex items-center gap-2"><RefreshCw size={11} /> Refund queue</div>
            <span className="font-mono text-xs text-[var(--text-tertiary)]">{refundQueue.length} item(s)</span>
          </div>
          <div className="mt-4 grid md:grid-cols-5 gap-2">
            <input className="input-dark md:col-span-1 text-xs" placeholder="Event ID" value={refundForm.eventId} onChange={(e) => setRefundForm((p) => ({ ...p, eventId: e.target.value }))} />
            <input className="input-dark md:col-span-1 text-xs" placeholder="Order ID" value={refundForm.orderId} onChange={(e) => setRefundForm((p) => ({ ...p, orderId: e.target.value }))} />
            <input className="input-dark md:col-span-1 text-xs" placeholder="Attendee email" value={refundForm.attendeeEmail} onChange={(e) => setRefundForm((p) => ({ ...p, attendeeEmail: e.target.value }))} />
            <input className="input-dark md:col-span-1 text-xs" type="number" min="0" placeholder="Amount" value={refundForm.amount} onChange={(e) => setRefundForm((p) => ({ ...p, amount: e.target.value }))} />
            <button className="btn-primary text-xs justify-center" onClick={requestRefund} disabled={refundBusy}>Request</button>
          </div>
          <div className="mt-5 divide-y divide-[var(--border-default)]">
            {refundQueue.map((row) => (
              <div key={row.id} className="py-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[var(--text-secondary)] font-mono text-xs">
                    {row.orderId || row.attendeeEmail || row.id} · ${Number(row.amount || 0).toFixed(2)} · {row.status}
                  </div>
                  {String(row.status || "").toLowerCase() === "pending" ? (
                    <div className="flex gap-2">
                      <button className="btn-secondary text-xs" onClick={() => decideRefund(row.id, "approve")} disabled={refundBusy}>Approve</button>
                      <button className="btn-secondary text-xs text-[var(--error)]" onClick={() => decideRefund(row.id, "reject")} disabled={refundBusy}>Reject</button>
                    </div>
                  ) : null}
                </div>
                <div className="text-xs text-[var(--text-tertiary)] mt-1">{row.reason || "No reason provided"} · requested {row.requestedAt || "—"}</div>
              </div>
            ))}
            {!refundQueue.length ? (
              <div className="py-3 text-sm text-[var(--text-secondary)]">No refund requests yet.</div>
            ) : null}
          </div>
        </div>

        <div className="surface p-6">
          <div className="flex items-center justify-between">
            <div className="text-overline flex items-center gap-2"><AlertTriangle size={11} /> Webhook health</div>
            <span className="font-mono text-xs text-[var(--text-tertiary)]">No webhook telemetry</span>
          </div>
          <div className="mt-6">
            <svg viewBox="0 0 700 140" className="w-full h-[140px]">
              {webhookSeries.map((point, i) => {
                const count = Number(point?.count || 0);
                const height = Math.max(4, Math.min(110, count));
                return <rect key={point?.id || i} x={i * 34} y={120 - height} width="24" height={height} fill="#27272A" opacity={0.9} />;
              })}
              {!webhookSeries.length ? (
                <rect x={0} y={60} width="700" height={24} fill="#27272A" opacity={0.4} />
              ) : null}
            </svg>
            <div className="flex justify-between text-[10px] font-mono text-[var(--text-tertiary)] mt-2"><span>{webhookSeries.length ? "Series start" : "No telemetry"}</span><span>{webhookSeries.length ? "latest" : "—"}</span></div>
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
            {serviceHealth.map((svc, idx) => (
              <div key={svc?.name || idx} className="bg-[var(--bg-surface)] p-4">
                <div className="text-overline">{svc?.name || "Service"}</div>
                <div className="font-mono text-sm mt-1" style={{ color: svc?.color || (svc?.status === "OK" ? "var(--primary)" : "#E25D3A") }}>{svc?.status || "Unknown"}</div>
              </div>
            ))}
            {!loading && !error && serviceHealth.length === 0 ? (
              <div className="bg-[var(--bg-surface)] p-4 col-span-1 sm:col-span-3 text-xs text-[var(--text-secondary)]">No service health data.</div>
            ) : null}
          </div>
        </div>
      </div>
      {error && <div className="mt-6 text-sm text-[var(--error)]">{error}</div>}
    </div>
  );
}

const CLOSE_CHECKLIST = [
  { id: "revenueReconciled", label: "Reconcile gross, net, and ticket sales", help: "Cross-check finance summary against ticket engine totals." },
  { id: "refundsReviewed", label: "Review refund queue and dispositions", help: "Ensure no pending manual approvals remain for this period." },
  { id: "payoutsMatched", label: "Match gateway payout with settlement records", help: "Validate settled transactions are represented in reports." },
  { id: "auditExported", label: "Export and archive audit evidence", help: "Store CSV export and controller notes for sign-off." },
];

function currentPeriod() {
  return new Date().toISOString().slice(0, 7);
}

function defaultCloseControls() {
  return {
    period: currentPeriod(),
    checklist: Object.fromEntries(CLOSE_CHECKLIST.map((item) => [item.id, false])),
    locked: false,
    lockedAt: null,
    lockedBy: null,
  };
}

function normalizeControls(raw) {
  const base = raw && typeof raw === "object" ? raw : {};
  const defaults = defaultCloseControls();
  return {
    period: String(base.period || defaults.period),
    checklist: Object.fromEntries(
      CLOSE_CHECKLIST.map((item) => [item.id, Boolean(base?.checklist?.[item.id])]),
    ),
    locked: Boolean(base.locked),
    lockedAt: base.lockedAt ? String(base.lockedAt) : null,
    lockedBy: base.lockedBy ? String(base.lockedBy) : null,
  };
}

function formatDateTime(value) {
  if (!value) return "just now";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "just now";
  return d.toLocaleString();
}

function KPI({ k, v, delta }) {
  return (
    <div className="bg-[var(--bg-surface)] p-6 min-w-0">
      <div className="text-overline truncate">{k}</div>
      <div className="font-mono text-3xl mt-2 truncate tabular-nums">{v}</div>
      <div className="text-xs font-mono text-[var(--primary)] mt-2 flex items-center gap-1 min-w-0"><TrendingUp size={11} className="shrink-0" /> <span className="truncate">{delta}</span></div>
    </div>
  );
}
